// src/components/StudentForm.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button, Input } from './ui';
import PaymentList from './PaymentList';

interface StudentFormData {
    person_code: string;
    name: string;
    gender: string;
    dharma_name: string;
    birth_date: string;
    mobile_phone: string;
    email: string;
    current_status: string;
}

const StudentForm: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    // Get the search params from the location state
    const fromSearch = location.state?.from?.search || '';

    const handleCancel = () => {
        // Navigate back to the list with the preserved search params
        navigate(`/?${fromSearch}`);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const url = isEditMode ? `/api/students/${id}` : '/api/students';
            const method = isEditMode ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                // Navigate back to the list with the preserved search params
                navigate(`/?${fromSearch}`);
            } else {
                const error = await response.json();
                throw new Error(error.message || '保存失败');
            }
        } catch (error) {
            console.error('Error saving student:', error);
        }
    };

    const [formData, setFormData] = useState<StudentFormData>({
        person_code: '',
        name: '',
        gender: '',
        dharma_name: '',
        birth_date: '',
        mobile_phone: '',
        email: '',
        current_status: ''
    });

    useEffect(() => {
        if (isEditMode) {
            fetchStudentData();
        }
    }, [id]);

    const fetchStudentData = async () => {
        try {
            const response = await fetch(`/api/students/${id}`);
            if (response.ok) {
                const data = await response.json();
                setFormData(data);
            }
        } catch (error) {
            console.error('Error fetching student:', error);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    return (
        <div className="p-4 space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>{isEditMode ? '编辑林员' : '新增林员'}</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">林员编码</label>
                            <Input
                                type="text"
                                name="person_code"
                                value={formData.person_code}
                                onChange={handleInputChange}
                                required
                                className="mt-1"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">姓名</label>
                                <Input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">法名</label>
                                <Input
                                    type="text"
                                    name="dharma_name"
                                    value={formData.dharma_name}
                                    onChange={handleInputChange}
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">性别</label>
                                <select
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleSelectChange}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                >
                                    <option value="">请选择</option>
                                    <option value="男">男</option>
                                    <option value="女">女</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">出生日期</label>
                                <Input
                                    type="date"
                                    name="birth_date"
                                    value={formData.birth_date}
                                    onChange={handleInputChange}
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">手机号码</label>
                                <Input
                                    type="tel"
                                    name="mobile_phone"
                                    value={formData.mobile_phone}
                                    onChange={handleInputChange}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">电子邮箱</label>
                                <Input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">当前状态</label>
                            <select
                                name="current_status"
                                value={formData.current_status}
                                onChange={handleSelectChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            >
                                <option value="">请选择</option>
                                <option value="在林">在林</option>
                                <option value="退林">退林</option>
                                <option value="除名">除名</option>
                                <option value="失联">失联</option>
                                <option value="出家">出家</option>
                                <option value="往生">往生</option>
                            </select>
                        </div>

                        <div className="flex justify-end space-x-4 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleCancel}
                            >
                                取消
                            </Button>
                            <Button
                                type="submit"
                                className="bg-blue-500 hover:bg-blue-600 text-white"
                            >
                                {isEditMode ? '更新' : '保存'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
            {isEditMode && (
                <PaymentList
                    studentId={parseInt(id as string)}
                    studentName={formData.name}
                />
            )}
        </div>
    );
};

export default StudentForm;